/**
 * The render kit's public surface.
 *
 *   mount(el, opts) -> Kit
 *   kit.pushSnapshot(s)     — one frame of sim truth; every SimEvent becomes motion
 *   kit.player              — the fixture player, so the whole kit is reviewable with no sim
 *
 * The kit owns the scene; the sim owns the truth. Nothing in here reads back into core.
 */

import { Group, Vector3, PointLight, Color } from 'three';
import type { MachineId, Snapshot, TokenState } from '../../shared/contract';
import { createStage, type Stage, type Quality } from './scene/stage';
import { CameraRig } from './scene/camera';
import { buildRoom, buildCroupier, buildSpieler, type Room, type Croupier } from './prefabs/room';
import { CABINET_BUILDERS, type Cabinet } from './prefabs/cabinets';
import { TokenSystem, rebase, type TokenView } from './tokens/tokens';
import { SpriteField, NormalBlending } from './fx/sprites';
import { CoinShower } from './fx/coins';
import { Anim } from './anim/tween';
import { dispatch, fxSignGag, type FxContext } from './anim/effects';
import { loadFonts, spark, setFontBase } from './art/textures';
import { NEON, MACHINE_LOOK } from './art/palette';
import { readPixelStats, type PixelStats } from './showroom/debug';
import { FixturePlayer } from './fixtures';

export interface MountOpts {
  quality?: Quality;
  size?: [number, number];
  shadows?: boolean;
  /** where the OFL fonts are served from (default /fonts/) */
  fontBase?: string;
  /** start the raf loop (screenshot gates drive frames by hand instead) */
  autoLoop?: boolean;
}

export interface Kit {
  stage: Stage;
  rig: CameraRig;
  room: Room;
  croupier: Croupier;
  cabinets: Map<MachineId, Cabinet>;
  tokens: TokenSystem;
  coins: CoinShower;
  anim: Anim;
  ctx: FxContext;
  player: FixturePlayer;

  pushSnapshot(s: Snapshot): void;
  /** focus a cabinet with the signature dolly-zoom */
  focus(id: MachineId | null): void;
  step(dt: number): void;
  render(): void;
  resize(w?: number, h?: number): void;
  stats(): { drawCalls: number; triangles: number; programs: number; tokens: number; coins: number; anims: number; packed: number };
  pixels(): PixelStats;
  say(text: string, sub?: string): void;
  dispose(): void;
}

/**
 * Build order, which is also aisle order. The FLIPPER takes the sixth slot — the far end
 * of the room, past the Preistheke, which is where the Saalchef's door is.
 *
 * It has to be in this list or it does not exist as geometry, and the failure mode is
 * quiet: `focus('flipper')` finds no cabinet, so it leaves the camera wherever it was and
 * the boss fight is played against a photograph of the slot machine. The audit caught it
 * — the Hausregeln board said DER SAALCHEF while the picture showed the bandit — and no
 * liveness gate ever could have, because the frame was a perfectly good frame.
 */
const ORDER: MachineId[] = ['pachinko', 'muenzschieber', 'bandit', 'roulette', 'greifer', 'flipper'];

export async function mount(el: HTMLElement, opts: MountOpts = {}): Promise<Kit> {
  if (opts.fontBase) setFontBase(opts.fontBase);
  await loadFonts();

  const stage = createStage(el, { quality: opts.quality, size: opts.size, shadows: opts.shadows });
  const rig = new CameraRig({ camera: stage.camera });

  // ---- world ---------------------------------------------------------------
  const room = buildRoom(stage.scene);

  const cabinets = new Map<MachineId, Cabinet>();
  ORDER.forEach((id, i) => {
    const c = CABINET_BUILDERS[id]();
    const slot = room.slots[i];
    c.group.position.copy(slot);
    // the aisle is not a shelf: every cabinet sits a little differently
    c.group.position.z += (i % 2 ? 1.4 : -1.2);
    c.group.rotation.y = (i - 2) * 0.05;
    stage.scene.add(c.group);
    cabinets.set(id, c);
  });

  const croupier = buildCroupier();
  croupier.group.position.set(62, 0, -20);
  croupier.group.rotation.y = -0.85;
  stage.scene.add(croupier.group);

  const spieler = buildSpieler();
  spieler.position.set(-58, 0, -2);
  spieler.rotation.y = 0.55;
  stage.scene.add(spieler);

  // ---- fx pools ------------------------------------------------------------
  const fxRoot = new Group();
  fxRoot.name = 'fx';
  stage.scene.add(fxRoot);

  const sparks = new SpriteField(spark(), 420).addTo(fxRoot);
  const smoke = new SpriteField(spark(), 180, NormalBlending).addTo(fxRoot);
  const coins = new CoinShower({ capacity: 260 }).addTo(fxRoot);
  const tokens = new TokenSystem({ capacity: 420, sparks, smoke }).addTo(fxRoot);

  const anim = new Anim();

  // ---- overlay (vignette + caption): DOM, because it is 2D and cheap --------
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:absolute;inset:0;pointer-events:none;opacity:0;' +
    'background:radial-gradient(ellipse at center, rgba(8,7,14,0) 38%, rgba(8,7,14,0.92) 100%);' +
    'transition:none;z-index:5';
  if (getComputedStyle(el).position === 'static') el.style.position = 'relative';
  el.appendChild(overlay);

  let sayFn: (t: string, s?: string) => void = () => { };

  const ctx: FxContext = {
    stage, anim, rig, room, croupier, tokens, sparks, smoke, coins, cabinets, fxRoot,
    active: null,
    origins: new Map<MachineId, Vector3>(),
    origin: new Vector3(),
    lockouts: new Map<MachineId, number>(),
    hud: {
      muenzen: 0, tickets: 0, glueck: 5, verdacht: 0, straehne: 1, floor: 1,
      topf01: 0.35, topfMax: 400, tilt01: 0.1, tick: 0,
    },
    say: (t, s) => sayFn(t, s),
    setVignette: (v: number) => { overlay.style.opacity = String(Math.max(0, Math.min(1, v))); },
  };

  /**
   * **contract v1** — remember where a cabinet stands in the sim's shared world.
   * Both `CabinetState` and the cheap `AisleCabinet` rows carry it, so a cabinet the camera
   * is not on still resolves its own coordinates (a `payoutStage` on the machine next door).
   */
  function noteOrigin(id: MachineId, o: [number, number, number]): Vector3 {
    let v = ctx.origins.get(id);
    if (!v) { v = new Vector3(); ctx.origins.set(id, v); }
    v.set(o[0], o[1], o[2]);
    return v;
  }

  // the two point lights follow the two nearest neon signs (ART-BIBLE §6)
  const [p1, p2] = stage.neonLights;
  function relight(): void {
    const near: { c: Cabinet; d: number }[] = [];
    for (const c of cabinets.values()) {
      near.push({ c, d: c.group.position.distanceTo(stage.camera.position) });
    }
    near.sort((a, b) => a.d - b.d);
    const put = (l: PointLight, c?: Cabinet) => {
      if (!c) { l.intensity = 0; return; }
      const look = MACHINE_LOOK[c.id];
      l.color = new Color(NEON[look.accent].core);
      l.position.set(c.group.position.x, c.group.position.y + c.size[1] * 0.95, c.group.position.z + 11);
      l.intensity = 34;
      l.distance = 46;
    };
    put(p1, near[0]?.c);
    put(p2, near[1]?.c);
  }

  // ---- state mirroring -----------------------------------------------------
  let lastTokens: TokenState[] = [];
  /**
   * The same tokens, moved out of the sim's world space into the focused cabinet's frame
   * (contract v1 — see `rebase`). Held for the frame so `step()` does not redo the work.
   */
  let lastViews: TokenView[] = [];

  const kit: Kit = {
    stage, rig, room, croupier, cabinets, tokens, coins, anim, ctx,
    player: null as unknown as FixturePlayer,

    pushSnapshot(s: Snapshot) {
      ctx.hud.muenzen = s.run.muenzen;
      ctx.hud.tickets = s.run.tickets;
      ctx.hud.glueck = s.run.glueck;
      ctx.hud.verdacht = s.run.verdacht;
      ctx.hud.floor = s.run.floor;
      ctx.hud.tick = s.tick;

      // the aisle rows are cheap and carry origins for cabinets the camera is not on
      for (const r of s.room) noteOrigin(r.machine, r.origin);

      if (s.run.activeMachine !== ctx.active) kit.focus(s.run.activeMachine);

      if (s.cabinet) {
        const origin = noteOrigin(s.cabinet.machine, s.cabinet.origin);
        if (s.cabinet.machine === ctx.active) ctx.origin.copy(origin);
        const c = cabinets.get(s.cabinet.machine);
        if (c) {
          ctx.hud.straehne = s.cabinet.straehne;
          ctx.hud.topf01 = s.cabinet.topf01;      // v1: the sim owns the balance, not us
          ctx.hud.topfMax = s.cabinet.topfMax;
          ctx.hud.tilt01 = s.cabinet.tilt01;
          c.topf.set(ctx.hud.topf01);
          // a running lockout animation owns the bubble until it releases it
          if (!ctx.lockouts.has(s.cabinet.machine)) c.tilt.set(s.cabinet.tilt01);
          c.marquee.setIntensity(s.cabinet.broken || !s.cabinet.powered ? 0.06 : 1);

          /**
           * The FLIPPER is the one cabinet whose `extra` the kit reads.
           *
           * LICHTREKLAME's cut 4 said per-machine payloads stay opaque in v0 — "the kit
           * animates from events and the typed fields only" — and that holds for the five
           * aisle machines, whose moving parts are all driven by events. It cannot hold
           * for a pinball table: the bats have a *continuous pose*, sixty times a second,
           * and there is no event stream that can carry an angle. A `bossPhase` event
           * cannot tell you where a flipper is.
           *
           * So this is the documented exception rather than a quiet one, and it is
           * narrow: two numbers and five booleans, read only while the boss table is the
           * focused cabinet.
           */
          if (s.cabinet.machine === 'flipper') {
            const ex = s.cabinet.extra as {
              flippers?: { side: string; angle: number }[];
              targets?: { down: boolean }[];
            } | null;
            const setF = c.api.setFlippers as ((l: number, r: number) => void) | undefined;
            const setT = c.api.setTargets as ((d: readonly boolean[]) => void) | undefined;
            if (ex && setF) setF(ex.flippers?.[0]?.angle ?? 0, ex.flippers?.[1]?.angle ?? 0);
            if (ex && setT) setT((ex.targets ?? []).map((t) => t.down));
          }
        }
        lastTokens = s.cabinet.tokens;
        // WORLD → the cabinet's frame. The single most important line in this file.
        // …and then the sim's frame → THIS MODEL's frame, which is a second, separate
        // reconciliation: see `Cabinet.tokenMount`. Applied here rather than in `focus()`
        // so it touches only snapshot-driven play; the showroom fixtures author their own
        // piles in art coordinates already and must not be shifted twice.
        const mount = cabinets.get(s.cabinet.machine)?.tokenMount;
        lastViews = rebase(lastTokens, origin);
        if (mount && (mount.x || mount.y || mount.z)) {
          for (const v of lastViews) {
            v.pos[0] += mount.x; v.pos[1] += mount.y; v.pos[2] += mount.z;
          }
        }
      } else {
        lastTokens = [];
        lastViews = [];
      }
      tokens.sync(lastViews);

      for (const ev of s.events) dispatch(ctx, ev);
    },

    focus(id: MachineId | null) {
      ctx.active = id;
      // Token coordinates are WORLD (contract v1) and are rebased by `toViews`; the pool
      // still hangs off the cabinet, which is what makes a travelling machine carry its
      // pile. When a cabinet moves, only its `origin` changes and the pile keeps its shape.
      const host = id ? cabinets.get(id)?.group : null;
      (host ?? fxRoot).add(tokens.group);
      ctx.origin.copy((id && ctx.origins.get(id)) || new Vector3());
      // a new machine is a new pile: no instance in the buffers is a fixed body yet
      tokens.clearPacked();
      if (!id) { rig.toAisle(0); }
      else {
        const c = cabinets.get(id);
        if (c) {
          const f = c.anchors.face.clone();
          c.group.updateMatrixWorld();
          // frame the whole machine, whatever size it is
          const span = Math.max(c.size[0], c.size[1] * 0.80);
          const w = c.group.localToWorld(f);
          w.y += c.size[1] * 0.10;         // lift toward the marquee: show the machine
          rig.toCabinet(w, { dist: 16 + span * 1.45, lift: 2.2 });
        }
      }
      // level of detail: the machine you are on gets everything
      let i = 0;
      for (const c of cabinets.values()) {
        c.setDetail(c.id === id ? 2 : (i < 3 ? 1 : 0));
        i++;
      }
    },

    step(dt: number) {
      const sdt = dt * stage.timescale;
      stage.beginFrame(sdt);
      anim.update(sdt);
      rig.update(dt);              // the camera lives outside slow-mo except when told
      room.update(sdt, stage.time);
      croupier.update(sdt, stage.time);
      for (const c of cabinets.values()) c.update(sdt, stage.time);
      tokens.sync(lastViews);
      tokens.update(sdt, stage.camera);
      coins.update(sdt, stage.camera);
      sparks.update(sdt, stage.camera);
      smoke.update(sdt, stage.camera);
      relight();
    },

    render() { stage.render(); },
    resize(w?: number, h?: number) { stage.resize(w, h); },

    stats() {
      const s = stage.stats();
      return {
        ...s,
        tokens: lastTokens.length,
        coins: coins.live,
        anims: anim.running,
        // instances the v1 `sediment` fast path skipped uploading this frame
        packed: tokens.skipped,
      };
    },

    pixels() { stage.render(); return readPixelStats(stage); },

    say(text: string, sub?: string) { sayFn(text, sub); },

    dispose() {
      anim.clear();
      tokens.dispose();
      overlay.remove();
      stage.dispose();
    },
  };

  // caption plumbing, set by the showroom
  Object.defineProperty(kit, 'onSay', {
    set(fn: (t: string, s?: string) => void) { sayFn = fn; },
  });

  kit.player = new FixturePlayer(kit);
  kit.focus(null);
  rig.toAisle(-6);
  rig.snap();
  anim.play(fxSignGag(ctx, 1));

  if (opts.autoLoop !== false) {
    let last = performance.now();
    const loop = () => {
      const now = performance.now();
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      kit.step(dt);
      kit.render();
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  return kit;
}

export { Vector3 };
